import { QueryClient } from '@tanstack/react-query';
import {
  Site,
  SiteAssignment,
  OrganizationMember,
  OrganizationInvitation,
  SiteStatsResponse,
  FindingsTrendResponse,
  AssignmentType,
} from '../../../lib/organizations';
import { TrendPeriodType } from '../../../lib/organizations';
import type { CompanyRoleView } from '../../../lib/rbac';
import { InspectionListItem } from '../../../lib/inspections';
import { Report } from '../../../lib/reports';
import { LayoutProps } from '../../../components/responsive/ResponsivePage';

/** 현장 상세 탭 — `?tab=` 쿼리 값과 같은 어휘(결재선 탭 직링크: `/sites/:siteId?tab=approval`). */
export type SiteDetailTab = 'basic' | 'overview' | 'management' | 'approval';

const SITE_DETAIL_TABS: readonly SiteDetailTab[] = ['basic', 'overview', 'management', 'approval'];

/** `?tab=` 쿼리 값을 탭으로 해석 — 모르는 값·부재는 기본 탭(basic). */
export function parseSiteDetailTab(value: string | null | undefined): SiteDetailTab {
  return SITE_DETAIL_TABS.find((tab) => tab === value) ?? 'basic';
}

/**
 * SiteDetail 페이지에서 사용하는 데이터 타입
 */
export interface SiteDetailData {
  site: Site;
  assignments: SiteAssignment[];
  members: OrganizationMember[];
  siteInvitations: OrganizationInvitation[];
  siteStats: SiteStatsResponse | undefined;
  findingsTrend: FindingsTrendResponse | undefined;
  recentInspections: InspectionListItem[] | undefined;
  recentReports: Report[] | undefined;
}

/**
 * SiteDetail 상태 관리 타입
 */
export interface SiteDetailState {
  // 접이식 섹션 상태
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;

  // 위험요인 추이 기간
  trendPeriod: TrendPeriodType;
  setTrendPeriod: (period: TrendPeriodType) => void;

  // 활성 탭
  activeTab: 'inspections' | 'reports' | 'tbm';
  setActiveTab: (tab: 'inspections' | 'reports' | 'tbm') => void;

  // 에러 상태
  error: string;
  setError: (error: string) => void;

  // 멤버 할당 모달
  showAssignModal: boolean;
  setShowAssignModal: (show: boolean) => void;
  selectedMemberId: string;
  setSelectedMemberId: (id: string) => void;
  isAssigning: boolean;
  assignMode: 'select' | 'invite';
  setAssignMode: (mode: 'select' | 'invite') => void;
  setPrimaryOnAssign: boolean;
  setSetPrimaryOnAssign: (value: boolean) => void;

  // 초대 폼 상태
  inviteIdentifier: string;
  setInviteIdentifier: (value: string) => void;
  invitePhone: string;
  setInvitePhone: (value: string) => void;
  /** 「소유자로 초대」 — payload `roleKeys: [OWNER_ROLE_KEY]`(P7-b-2, 옛 role select의 OWNER) */
  inviteAsOwner: boolean;
  setInviteAsOwner: (asOwner: boolean) => void;
  /** 조직 역할 멀티셀렉트(멤버 관리 초대 모달과 같은 축) — 기본 `member`. 소유자로 초대하면 무시된다 */
  inviteRoleKeys: string[];
  toggleInviteRoleKey: (roleKey: string) => void;
  inviteRoleOptions: CompanyRoleView[];
  isLoadingInviteRoles: boolean;
  isInviting: boolean;

  // 할당 해제
  unassignTarget: SiteAssignment | null;
  setUnassignTarget: (target: SiteAssignment | null) => void;
  isUnassigning: boolean;

  // 초대 관련
  isResending: string | null;
  invitationCancelTarget: OrganizationInvitation | null;
  setInvitationCancelTarget: (target: OrganizationInvitation | null) => void;
  isCancellingInvitation: boolean;

  // 담당자 상세 모달
  selectedAssignment: SiteAssignment | null;
  setSelectedAssignment: (assignment: SiteAssignment | null) => void;

  // 상세 탭
  detailTab: SiteDetailTab;
  setDetailTab: (tab: SiteDetailTab) => void;
}

/**
 * SiteDetail 핸들러 타입
 */
export interface SiteDetailHandlers {
  handleAssign: () => Promise<void>;
  handleInvite: () => Promise<void>;
  closeAssignModal: () => void;
  handleUnassign: () => Promise<void>;
  handleResendInvitation: (invitation: OrganizationInvitation) => Promise<void>;
  handleCancelInvitation: () => Promise<void>;
  handleUpdateAssignmentType: (assignmentType: AssignmentType) => Promise<void>;
}

/**
 * SiteDetail 권한 정보
 */
export interface SiteDetailPermissions {
  /**
   * 현장 관리 권한(`site.manage`, `usePermissions().canManageSites`) — 현장 정보·담당자(SiteContact)·통계·장소·공정·설비 편집
   * 어포던스. 옛 `role === 'OWNER'`(P4 ②). 하위 섹션들의 prop 이름은 호환용 `isOwner`로 남아 있지만 값은 이것이다.
   */
  canManageSite: boolean;
  /**
   * 점검 담당자 추가·해제 — 서버가 owner 전용을 유지하므로(현행 넓이 보존, sites.service assignMember/unassignMember
   * `isOwnerMember`) 값은 소유자 성질(`usePermissions().isOwner`). `site.assign` 키로 넓히는 것은 정책 결정(P7 전 사용자 판단).
   */
  canAssign: boolean;
  /** 멤버 초대(`member.invite`) — 초대 현황 조회·재발송·취소 */
  canInvite: boolean;
  /** 결재라인 관리(`approval.line.manage`) — 현장 결재선 기본값 편집(라운드 ③ 도메인, 여기서는 키만 읽는다) */
  canManageApprovalLines: boolean;
  isPrimaryAssignee: boolean;
  /** 현장 정보 수정 = `canManageSite || isPrimaryAssignee` — 서버 `SitesService.update`와 같은 OR 판정 */
  canEdit: boolean;
  unassignedMembers: OrganizationMember[];
}

/**
 * SiteDetail 레이아웃 Props
 */
export interface SiteDetailLayoutProps extends LayoutProps<SiteDetailData> {
  companyId: string;
  siteId: string;
  memberId: string | undefined;
  state: SiteDetailState;
  handlers: SiteDetailHandlers;
  permissions: SiteDetailPermissions;
  queryClient: QueryClient;
  navigate: (path: string) => void;
}
